import { z } from "zod";

/**
 * Shared DTO cho Admin Outbox Monitor & Dispatcher Control (US-AE7.F1-0023):
 *  - @UC9060 outbox-monitor    — GET  /admin/outbox (list event + lag/pending/failed summary).
 *  - @UC9061 dispatcher-control — POST /admin/outbox/channels/:channel/{pause,resume} + /admin/outbox/:id/replay.
 *
 * Một nguồn sự thật shape cho api (S01) + admin web (S03) — tránh drift DTO ở 2 nơi (DRY).
 *
 * BẢO MẬT / BẤT BIẾN (invariants):
 * - no-SMS (C1): enum kênh = in_app | push | zalo | email | teams — KHÔNG sms (khớp Notification.channel).
 * - no-PII (C2): OutboxRow CHỈ metadata vận hành (eventType/channel/status/attempts/thời điểm) — KHÔNG
 *   payload thô / nội dung thông báo (redaction §19).
 * - `.strict()` — reject field lạ tại biên. Cursor opaque string đối xứng common/pagination/cursor.
 * - G2: DTO chỉ mô tả ĐỌC (monitor) + đổi trạng thái dispatch (pause/replay) — KHÔNG field mutating ledger.
 */

/** Max page-size bảng outbox events. Default (20) do backend áp khi vắng. */
export const ADMIN_OUTBOX_MAX_LIMIT = 100;

/**
 * Kênh dispatch hợp lệ (khớp Notification.channel + ChannelDispatchState). no-SMS (C1). Dùng cho:
 *  - filter list (OutboxListQuery.channel);
 *  - param :channel của pause/resume (ngoài tập → 422 tại biên);
 *  - badge trạng thái kênh trên UI.
 */
export const outboxChannelSchema = z.enum(["in_app", "push", "zalo", "email", "teams"]);
export type OutboxChannel = z.infer<typeof outboxChannelSchema>;

/**
 * Trạng thái 1 outbox event (khớp OutboxEvent.status):
 *  - `pending`     : chờ dispatch (tính vào lag nếu là cũ nhất).
 *  - `dispatched`  : đã gửi (Notification tạo / adapter chạy xong).
 *  - `failed`      : dispatch lỗi → worker tự retry theo backoff tới `nextRetryAt`.
 *  - `dead_letter` : quá 5 lần retry vẫn lỗi → worker KHÔNG tự retry nữa, chờ admin replay tay
 *                    (US-AE7.F1-0152, @UC9165).
 */
export const outboxStatusSchema = z.enum(["pending", "dispatched", "failed", "dead_letter"]);
export type OutboxStatus = z.infer<typeof outboxStatusSchema>;

/**
 * Một dòng outbox event (@UC9060) — READ-ONLY, KHÔNG payload/PII thô (chỉ metadata vận hành).
 *  - `channel` null = event cũ (trước migration channel) — KHÔNG bị pause (fail-safe).
 *  - `attempts` — số lần đã thử dispatch (chẩn đoán).
 *  - lifecycle (US-AE7.F1-0152, @UC9165) — null = event cũ trước migration (fail-safe forward):
 *    - `lastError`      : message lỗi ĐÃ redact server-side (KHÔNG PII: to/token/link/payload) — C2.
 *    - `nextRetryAt`    : ISO — thời điểm worker sẽ pick lại (backoff mũ); null nếu không chờ retry.
 *    - `failedAt`       : ISO — lần fail gần nhất.
 *    - `deadLetteredAt` : ISO — thời điểm chuyển dead_letter (quá 5 lần retry); null nếu chưa.
 */
export const outboxRowSchema = z
  .object({
    id: z.string(),
    eventType: z.string(),
    channel: outboxChannelSchema.nullable(),
    status: outboxStatusSchema,
    attempts: z.number().int().min(0),
    lastError: z.string().nullable(),
    nextRetryAt: z.string().nullable(),
    failedAt: z.string().nullable(),
    deadLetteredAt: z.string().nullable(),
    createdAt: z.string(),
    dispatchedAt: z.string().nullable(),
  })
  .strict();
export type OutboxRow = z.infer<typeof outboxRowSchema>;

/**
 * Tóm tắt outbox (@UC9060, AC1). `lagSeconds` = now − createdAt của event pending CŨ NHẤT (0 nếu
 * không có pending). Expose cho AE9 alert lag>5' (task này KHÔNG tự gửi alert — chỉ phơi số).
 */
export const outboxSummarySchema = z
  .object({
    pending: z.number().int().min(0),
    failed: z.number().int().min(0),
    // deadLetter — số event quá 5 lần retry, chờ admin replay tay (US-AE7.F1-0152, @UC9165).
    deadLetter: z.number().int().min(0),
    lagSeconds: z.number().int().min(0),
  })
  .strict();
export type OutboxSummary = z.infer<typeof outboxSummarySchema>;

/**
 * Query GET /admin/outbox — filter status/channel + cursor pagination.
 * `status` dùng chung `outboxStatusSchema` ⇒ chấp nhận cả `failed` và `dead_letter` (@UC9165).
 */
export const outboxListQuerySchema = z
  .object({
    status: outboxStatusSchema.optional(),
    channel: outboxChannelSchema.optional(),
    /**
     * 🔴 US-AE16.F5-0373-S03 (@UC9646) — lọc theo LOẠI SỰ KIỆN.
     *
     * Repo đã từng có **19–21 event mồ côi** rơi vào `dead_letter`, trong đó có `group.owner_transferred`
     * (chuyển quyền sở hữu nhóm). Nhìn 300 dòng `dead_letter` mà không lọc được theo loại thì không
     * biết **loại nào đang hỏng** — biết "có lỗi" nhưng không biết lỗi ở đâu là không hành động được.
     *
     * ≤128 ký tự (tên event dài nhất trong registry còn xa mức này); rác/quá dài ⇒ **400 tại biên**,
     * KHÔNG để lọt xuống service thành 500.
     */
    eventType: z.string().trim().min(1).max(128).optional(),
    cursor: z.string().max(512).optional(),
    limit: z.coerce.number().int().min(1).max(ADMIN_OUTBOX_MAX_LIMIT).optional(),
  })
  .strict();
export type OutboxListQuery = z.infer<typeof outboxListQuerySchema>;

/** Envelope bảng outbox `{ data, summary, pageInfo }`. */
export const outboxListResponseSchema = z
  .object({
    data: z.array(outboxRowSchema),
    summary: outboxSummarySchema,
    pageInfo: z.object({
      hasNext: z.boolean(),
      nextCursor: z.string().nullable(),
    }),
  })
  .strict();
export type OutboxListResponse = z.infer<typeof outboxListResponseSchema>;

/**
 * Trạng thái pause 1 kênh (GET summary con + phản hồi pause/resume). `paused` = dispatch đang hoãn.
 */
export const channelStateSchema = z
  .object({
    channel: outboxChannelSchema,
    paused: z.boolean(),
  })
  .strict();
export type ChannelState = z.infer<typeof channelStateSchema>;

/**
 * Envelope GET /admin/outbox/channels (@UC9060 read) — trạng thái pause TẤT CẢ kênh hợp lệ từ SERVER
 * (nguồn sự thật cho UI toolbar: reload / thao tác admin khác vẫn hiển thị đúng, không optimistic-local).
 */
export const channelStatesResponseSchema = z
  .object({
    data: z.array(channelStateSchema),
  })
  .strict();
export type ChannelStatesResponse = z.infer<typeof channelStatesResponseSchema>;

/**
 * Kết quả POST /admin/outbox/channels/:channel/{pause,resume} (@UC9061, AC2). Trả trạng thái kênh sau
 * thao tác. Kênh KHÁC KHÔNG đổi; event KHÔNG mất (pause chỉ hoãn).
 */
export const channelPauseResponseSchema = channelStateSchema;
export type ChannelPauseResponse = z.infer<typeof channelPauseResponseSchema>;

/**
 * Kết quả POST /admin/outbox/:id/replay (@UC9061, AC3 idempotent).
 * Replay chấp nhận nguồn `failed` HOẶC `dead_letter` (US-AE7.F1-0152, @UC9165, AC6) → đặt lại `pending`
 * (reset nextRetryAt, GIỮ attempts để truy vết).
 *  - `requeued` — true = event failed/dead_letter đã đặt lại pending (dispatcher xử lý lượt sau).
 *  - `alreadyRequeued` — true = event KHÔNG ở trạng thái failed/dead_letter (đã pending/dispatched) → no-op
 *                        idempotent (replay lần 2 KHÔNG nhân đôi). UI hiển thị "đã đưa vào hàng đợi".
 *  - `status` — trạng thái outbox hiện tại sau thao tác.
 */
export const replayResponseSchema = z
  .object({
    id: z.string(),
    requeued: z.boolean(),
    alreadyRequeued: z.boolean(),
    status: outboxStatusSchema,
  })
  .strict();
export type ReplayResponse = z.infer<typeof replayResponseSchema>;

/**
 * Kết quả POST /admin/outbox/:id/dispatch-now (US-AE7.F1-0152, @UC9165, AC7) — force-dispatch 1 event.
 * Chỉ đặt `nextRetryAt=now`/`pending` để worker lượt kế pick SỚM — KHÔNG gửi đồng bộ trong request admin
 * (tránh block HTTP + rò lỗi adapter ra response).
 *  - `queued` — true = "đã xếp gửi ngay" (KHÔNG phải "đã gửi xong"); worker sẽ dispatch lượt kế.
 *  - `status` — trạng thái outbox hiện tại sau thao tác (thường `pending`).
 */
export const forceDispatchResponseSchema = z
  .object({
    id: z.string(),
    status: outboxStatusSchema,
    queued: z.boolean(),
  })
  .strict();
export type ForceDispatchResponse = z.infer<typeof forceDispatchResponseSchema>;
